import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FlexBox, GridBox, useBreakpoint, useToasts } from '@brg/ui';
import {  API, Competition, Endpoints } from '@brg/api';
import { Input } from '@brg/input';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst, ModalControllerContainer, useModal } from 'core';
import { AppTester } from 'supporting';
import { 
    SendButton, 
    Wrapper , 
    ContentContainer,
    MainTitle, 
    LegalsLabel,
    EduSecetWrapper
} from '../styles';
import { ProjectEditFrameCompetitionRequestSended } from 'shared/components/error-frames';
import { DamuseumDropdown } from 'shared/components/DropdownSearchBar/DamuseumDropdown';
import { LabelRedStar } from 'shared/pages/designerEditView/styles';
import { CustomForm } from '../competitionLegalsModal/components/CustomForm';
import { COMPETITION_TYPE } from '_competition/const';

export enum DOCUMENT_VARIANT {
    none = 'none',
    short = 'short',
    full = 'full'
}

const geniusPlaceCompetitionIds = [
    'dbb1373cffef4117b49b7ea2c8363113',
    'ec0d7a8ca18d40659e00922801da2e87',
    '8539fee964154878ad7a990b0aab2e43',
    'cdd00b10c69846b7aed8998c0f836c0c',
    'b0ab7de5075f41e89a77b9313d25bad0',
    'a46139a3186243a5a6a68a63cd31220c',
    'b7ed9c557c624675b8f26e8916dae20f',
    '42280da33b774c4daa7ce351482330b0'
];

const nominationList = {
    value_1: 'INTERIOR',
    value_2: 'ARCH',
    value_3: 'DESIGN',
    value_4: 'Art&Craft',
    value_5: 'Future feat. Школа Дизайна НИУ ВШЭ',
}

const sNominationsSubList: Record<string, string[]> = {
    "INTERIOR": ["квартира/загородный дом","квартира до 50м2","ресторан, кафе, отель","магазин, бутик, бьюти-пространство","общественное пространство" ],
    "ARCH": ["загородный дом", "малый объект, ландшафт и благоустройство","выставочная архитектура"],
    "DESIGN": ["Mass production","Art&Design"],
    "Art&Craft": [],
    "Future feat. Школа Дизайна НИУ ВШЭ": [],
}


export const getDocumentVariant = (c: Competition) => {
    const rawType = c?.competitionType;
    const hasCompetitionType = Number.isInteger(rawType);
    const isWithQuestionnaire = hasCompetitionType
        ? rawType === COMPETITION_TYPE.WithQuestionnaire || !!c?.isPaidCompetition
        : !!c?.nominations?.[0]?.needToFillForm;
    if (isWithQuestionnaire && !!c?.nominations?.[0]?.documentsIds?.length) {
        return DOCUMENT_VARIANT.full;
    }
    if (isWithQuestionnaire && c?.nominations?.[0]?.documentsIds?.length === 0) {
        return DOCUMENT_VARIANT.short;
    }
    return DOCUMENT_VARIANT.none;
};
/**
 * @param Модалка для конкурса 1635cbc48d0a4d8aab84f91cfa66afa6 @Artem
 */

interface ICustomLegalsPropsNew {
    nomination: string;
    subnomination: string;
    names: string;
    vocation: string;
    phone: string;
    email: string;
    site: string;
    social: string;
    portraitLink: string;
    objectPhotoLink: string;
    media: string;
    photographer: string;
    shootingStylist: string;
    year: string;
    city: string;
    area: string;
    brands: string;
    description: string;
}

export const CustomLegalsModal: FC<{
        projects: string[],
        nominationIds: string[],
        callback: () => void,
        competitionId?: string
    }> =
    ({projects, nominationIds, competitionId, callback }) => {
    const { t, i18n: { language } } = useTranslation();
    const { error } = useToasts();
    const theme = useTheme();
    const form = useRef(null);
    const modal = useModal();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1 );

    const [requestSended, setRequestSended] = useState(false);
    const [agreeSign, setAgreeSign] = useState(false);
    const [agreeData, setAgreeData] = useState(null);
    const [data, setData] = useState<ICustomLegalsPropsNew>({
        nomination: '',
        subnomination: '',
        names: '',
        vocation: '',
        phone: '',
        email: '',
        site: '',
        social: '',
        portraitLink: '',
        objectPhotoLink: '',
        media: '',
        photographer: '',
        shootingStylist: '',
        year: '',
        city: '',
        area: '',
        brands: '',
        description: '',
    });

    const isGeo = AppTester.isGeograffee();
    const isDafes = AppTester.isGeoDafes()

    const someNomination = useMemo(() => (nominationIds?.length > 1), [nominationIds])

    const subNominationsOptions = useMemo(() => (data.nomination !== '' ? sNominationsSubList[data.nomination] : {}), [data])

    const isGeniusPlaceCompetition = competitionId && geniusPlaceCompetitionIds.includes(competitionId)

    const handleFormFieldChange = useCallback((fieldName: string, value?: any) => (e: any) => {
        if (fieldName === 'nomination') {
            setData((prev: any) => { return {...prev, [fieldName]: e.target.value, subnomination: ''}})  
        } else {
            setData((prev: any) => { return {...prev, [fieldName]: e.target.value}})
        }
    }
    ,[])

    const handleAgreeDataSign = (flag: boolean, data: any ) => {
        setAgreeSign(flag)
        setAgreeData(data)
    }

    const onSaveClick = useCallback((e: React.SyntheticEvent) => {
        e.preventDefault();
        
        if (!isGeniusPlaceCompetition) {

            if (data.nomination === '') {
                error(t('reports.nomination_exist'));
                return
            } else if (data.subnomination === '' && !['Art&Craft', 'Future feat. Школа Дизайна НИУ ВШЭ']?.includes(data.nomination)) {
                error(t('reports.subnomination_exist'));
                return
            } else if (!agreeSign) {
                error(t('reports.agree_sign'));
                return
            }
        }

        const confirm = window.confirm('Вы подтверждаете отправку заявки?')

    
        if (data && form && form.current && confirm) {
            const formData = new FormData(form.current);
            const formatted = 
                Object.entries(data).map(([key,val]) => ({
                    Title: key,
                    Value: val
                }))

            formData.append('Fields', JSON.stringify(formatted));

            formData.set('NominationId', nominationIds?.[0]);

            const documentsFormat = agreeData ? Object.entries(agreeData)?.map(([k,v]) => {
                return {
                    'Name': k,
                    'IsSigned': v
                }
            }) : null

            if (documentsFormat) {
                formData.append('Documents',  JSON.stringify(documentsFormat))
            }

            API.fetch(
                Endpoints.competitions + '/api/Competition/PreRegistration', {
                    method: "POST",
                    body: formData 
            })
            .then((r) => {
                if (r.status === 200) {
                    setRequestSended(true);
                }
            })
            .catch((e) => error(String(e)))
        }
    }, [
        data, 
        agreeSign,
        someNomination,
        nominationIds, 
        t, 
        callback, 
        error
    ]);

    const defaultNominationId = useMemo(() => nominationIds?.[0] , [nominationIds])

    if (requestSended) {
        return <ProjectEditFrameCompetitionRequestSended onBackClick={() => modal.hide()}  />
    }

    return <ModalControllerContainer>
        <Wrapper direction='column' style={{ margin: isMobile ? '12px auto' : '88px auto', overflowY: "auto", overflowX: 'hidden' }} isGeo={isGeo && !isDafes} >
            <ContentContainer>
            <MainTitle>{t('competition_edit.form.form_name')}</MainTitle>
            <form ref={form} onSubmit={(e) => onSaveClick(e)} encType="multipart/form-data" id="submit-form">
                <input type='text' name='NominationId' value={defaultNominationId} hidden />
                <FlexBox justify={'center'} direction='column' directionMobile='column' offsetRight={20} offsetRightMobile={1}>
                    {!isGeniusPlaceCompetition && 
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                                    <LegalsLabel>
                                        {t('competition_edit.form.nomination')}
                                        <LabelRedStar>*</LabelRedStar>
                                    </LegalsLabel>
                                    <EduSecetWrapper>
                                        <DamuseumDropdown 
                                            editing={true}
                                            onAddClick={(ev:any) => console.log('add', ev)}
                                            onChooseClick={(ev: any) => console.log('choose',ev)}
                                            currentValue={data.nomination}
                                            valueList={Object.values(nominationList)}
                                            onChange={handleFormFieldChange('nomination')}
                                        />
                                    </EduSecetWrapper>
                                </GridBox>
                    }
                    {!isGeniusPlaceCompetition && (
                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                            <LegalsLabel>
                                {t('competition_edit.form.nomination')}
                                <LabelRedStar>*</LabelRedStar>
                            </LegalsLabel>
                            <EduSecetWrapper>
                                <DamuseumDropdown 
                                    editing={true}
                                    onAddClick={(ev:any) => console.log('add', ev)}
                                    onChooseClick={(ev: any) => console.log('choose',ev)}
                                    currentValue={data.subnomination} 
                                    placeholder='нет подноминаций'
                                    valueList={Object.values(subNominationsOptions)}
                                    onChange={handleFormFieldChange('subnomination')}
                                />
                            </EduSecetWrapper>
                        </GridBox>
                    )
                    }
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.' + (isGeniusPlaceCompetition? 'fullname' : 'names'))}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            required
                            onChange={handleFormFieldChange('names')}
                            value={data.names}
                            data-type="names"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.phone_number')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            type='tel'
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            pattern="[0-9]{1}[0-9]{3}[0-9]{3}[0-9]{4}"
                            onChange={handleFormFieldChange('phone')}
                            value={data.phone}
                            data-type="phone"
                            maxLength={100}
                            required
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.email')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            type={'email'}
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('email')}
                            value={data.email}
                            data-type="email"
                            maxLength={100}
                            required
                        />
                    </GridBox>
                    {!isGeniusPlaceCompetition && <>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.site')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('site')}
                            value={data.site}
                            data-type="site"
                            maxLength={100}
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.social')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('social')}
                            value={data.social}
                            data-type="social"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.portraitLink')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('portraitLink')}
                            value={data.portraitLink}
                            data-type="portraitLink"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.objectPhotoLink')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('objectPhotoLink')}
                            value={data.objectPhotoLink}
                            data-type="objectPhotoLink"
                            required
                            pattern='^(file|ftp|http|https):\/\/[^ "]+$'
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.media')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('media')}
                            value={data.media}
                            data-type="media"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.photographer')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('photographer')}
                            value={data.photographer}
                            data-type="photographer"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.shootingStylist')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('shootingStylist')}
                            value={data.shootingStylist}
                            data-type="shootingStylist"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.year')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            pattern='[0-9]{4}'
                            onChange={handleFormFieldChange('year')}
                            value={data.year}
                            data-type="year"
                            required
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.city')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('city')}
                            value={data.city}
                            data-type="city"
                            required
                            pattern='^[a-zA-Zа-яА-ЯёЁ\s-]+$'
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.area')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('area')}
                            value={data.area}
                            data-type="area"
                            required
                            pattern='^[a-zA-Zа-яА-ЯёЁ0-9\s\-.,⁰¹²³⁴⁵⁶⁷⁸⁹]+$'
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.brands')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('brands')}
                            value={data.brands}
                            data-type="brands"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.description')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Textarea
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('description')}
                            value={data.description}
                            data-type="description"
                            required
                        />
                    </GridBox>
                    </>}
                </FlexBox>
                {!isGeniusPlaceCompetition && (
                    <CustomForm
                        onAgreeSign={(flag: boolean, data: any) => handleAgreeDataSign(flag, data)}
                        checkboxes={
                            [
                                {
                                    text: (language === 'ru' ? (
                                        <span>Я соглашаюсь с <a href={'https://deziiign.com/legals/offer'} target={'_BLANK'}>Пользовательским соглашением ресурсов Deziiign</a></span>
                                    ) : (
                                        <span>I agree to <a href={'https://deziiign.com/legals/offer'} target={'_BLANK'}>the User Agreement of Deziiign Resources</a></span>
                                    ))
                                },
                                {
                                    text: (language === 'ru' ? (
                                        <span>Я соглашаюсь с <a href={'https://deziiign.com/legals/privacy'} target={'_BLANK'}>Политикой конфиденциальности и обработки персональных данных ресурсов Deziiign</a></span>
                                    ) : (
                                        <span>I agree to <a href={'https://deziiign.com/legals/privacy'} target={'_BLANK'}>the Privacy Policy and processing of personal data of Deziiign resources</a></span>
                                    ))
                                },
                                {
                                    text: (language === 'ru' ? (
                                        <span>Я соглашаюсь с <a href={'https://www.interior.ru/design/19375.html'} target={'_BLANK'}>Политикой персональных данных ресурса Interior.ru</a></span>
                                    ) : (
                                        <span>I agree to <a href={'https://www.interior.ru/design/19375.html'} target={'_BLANK'}>the Personal Data Policy of the Interior.ru resource</a></span>
                                    ))
                                }
                            ]
                        }
                    />
                )}

                <FlexBox justify={'flex-end'} direction='row' directionMobile={isMobile ? 'column' : 'row'} offsetTop={40} offsetRight={20} offsetRightMobile={1}>
                    <SendButton isGreen={isGeo && !isDafes} type='submit' mr={"10"}>
                        Отправить
                    </SendButton>
                </FlexBox>
            </form>
            </ContentContainer>
        </Wrapper>
    </ModalControllerContainer>

};
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { FlexBox, GridBox, useBreakpoint, useToasts } from '@brg/ui';
import {  API, Competition, Endpoints } from '@brg/api';
import { Input } from '@brg/input';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LayoutConst, ModalControllerContainer, useModal } from 'core';
import { AppTester } from 'supporting';
import { 
    SendButton, 
    Wrapper , 
    ContentContainer,
    MainTitle, 
    LegalsLabel,
    EduSecetWrapper
} from '../styles';
import { ProjectEditFrameCompetitionRequestSended } from 'shared/components/error-frames';
import { DamuseumDropdown } from 'shared/components/DropdownSearchBar/DamuseumDropdown';
import { LabelRedStar } from 'shared/pages/designerEditView/styles';
import { CustomForm } from '../competitionLegalsModal/components/CustomForm';
import { COMPETITION_TYPE } from '_competition/const';

export enum DOCUMENT_VARIANT {
    none = 'none',
    short = 'short',
    full = 'full'
}

const geniusPlaceCompetitionIds = [
    'dbb1373cffef4117b49b7ea2c8363113',
    'ec0d7a8ca18d40659e00922801da2e87',
    '8539fee964154878ad7a990b0aab2e43',
    'cdd00b10c69846b7aed8998c0f836c0c',
    'b0ab7de5075f41e89a77b9313d25bad0',
    'a46139a3186243a5a6a68a63cd31220c',
    'b7ed9c557c624675b8f26e8916dae20f',
    '42280da33b774c4daa7ce351482330b0'
];

const nominationList = {
    value_1: 'INTERIOR',
    value_2: 'ARCH',
    value_3: 'DESIGN',
    value_4: 'Art&Craft',
    value_5: 'Future feat. Школа Дизайна НИУ ВШЭ',
}

const sNominationsSubList: Record<string, string[]> = {
    "INTERIOR": ["квартира/загородный дом","квартира до 50м2","ресторан, кафе, отель","магазин, бутик, бьюти-пространство","общественное пространство" ],
    "ARCH": ["загородный дом", "малый объект, ландшафт и благоустройство","выставочная архитектура"],
    "DESIGN": ["Mass production","Art&Design"],
    "Art&Craft": [],
    "Future feat. Школа Дизайна НИУ ВШЭ": [],
}


export const getDocumentVariant = (c: Competition) => {
    const rawType = c?.competitionType;
    const hasCompetitionType = Number.isInteger(rawType);
    const isWithQuestionnaire = hasCompetitionType
        ? rawType === COMPETITION_TYPE.WithQuestionnaire || !!c?.isPaidCompetition
        : !!c?.nominations?.[0]?.needToFillForm;
    if (isWithQuestionnaire && !!c?.nominations?.[0]?.documentsIds?.length) {
        return DOCUMENT_VARIANT.full;
    }
    if (isWithQuestionnaire && c?.nominations?.[0]?.documentsIds?.length === 0) {
        return DOCUMENT_VARIANT.short;
    }
    return DOCUMENT_VARIANT.none;
};
/**
 * @param Модалка для конкурса 1635cbc48d0a4d8aab84f91cfa66afa6 @Artem
 */

interface ICustomLegalsPropsNew {
    nomination: string;
    subnomination: string;
    names: string;
    vocation: string;
    phone: string;
    email: string;
    site: string;
    social: string;
    portraitLink: string;
    objectPhotoLink: string;
    media: string;
    photographer: string;
    shootingStylist: string;
    year: string;
    city: string;
    area: string;
    brands: string;
    description: string;
}

export const CustomLegalsModal: FC<{
        projects: string[],
        nominationIds: string[],
        callback: () => void,
        competitionId?: string
    }> =
    ({projects, nominationIds, competitionId, callback }) => {
    const { t, i18n: { language } } = useTranslation();
    const { error } = useToasts();
    const theme = useTheme();
    const form = useRef(null);
    const modal = useModal();
    const isMobile = useBreakpoint(LayoutConst.screen.s -1 );

    const [requestSended, setRequestSended] = useState(false);
    const [agreeSign, setAgreeSign] = useState(false);
    const [agreeData, setAgreeData] = useState(null);
    const [data, setData] = useState<ICustomLegalsPropsNew>({
        nomination: '',
        subnomination: '',
        names: '',
        vocation: '',
        phone: '',
        email: '',
        site: '',
        social: '',
        portraitLink: '',
        objectPhotoLink: '',
        media: '',
        photographer: '',
        shootingStylist: '',
        year: '',
        city: '',
        area: '',
        brands: '',
        description: '',
    });

    const isGeo = AppTester.isGeograffee();
    const isDafes = AppTester.isGeoDafes()

    const someNomination = useMemo(() => (nominationIds?.length > 1), [nominationIds])

    const subNominationsOptions = useMemo(() => (data.nomination !== '' ? sNominationsSubList[data.nomination] : {}), [data])

    const isGeniusPlaceCompetition = competitionId && geniusPlaceCompetitionIds.includes(competitionId)

    const handleFormFieldChange = useCallback((fieldName: string, value?: any) => (e: any) => {
        if (fieldName === 'nomination') {
            setData((prev: any) => { return {...prev, [fieldName]: e.target.value, subnomination: ''}})  
        } else {
            setData((prev: any) => { return {...prev, [fieldName]: e.target.value}})
        }
    }
    ,[])

    const handleAgreeDataSign = (flag: boolean, data: any ) => {
        setAgreeSign(flag)
        setAgreeData(data)
    }

    const onSaveClick = useCallback((e: React.SyntheticEvent) => {
        e.preventDefault();
        
        if (!isGeniusPlaceCompetition) {

            if (data.nomination === '') {
                error(t('reports.nomination_exist'));
                return
            } else if (data.subnomination === '' && !['Art&Craft', 'Future feat. Школа Дизайна НИУ ВШЭ']?.includes(data.nomination)) {
                error(t('reports.subnomination_exist'));
                return
            } else if (!agreeSign) {
                error(t('reports.agree_sign'));
                return
            }
        }

        const confirm = window.confirm('Вы подтверждаете отправку заявки?')

    
        if (data && form && form.current && confirm) {
            const formData = new FormData(form.current);
            const formatted = 
                Object.entries(data).map(([key,val]) => ({
                    Title: key,
                    Value: val
                }))

            formData.append('Fields', JSON.stringify(formatted));

            formData.set('NominationId', nominationIds?.[0]);

            const documentsFormat = agreeData ? Object.entries(agreeData)?.map(([k,v]) => {
                return {
                    'Name': k,
                    'IsSigned': v
                }
            }) : null

            if (documentsFormat) {
                formData.append('Documents',  JSON.stringify(documentsFormat))
            }

            API.fetch(
                Endpoints.competitions + '/api/Competition/PreRegistration', {
                    method: "POST",
                    body: formData 
            })
            .then((r) => {
                if (r.status === 200) {
                    setRequestSended(true);
                }
            })
            .catch((e) => error(String(e)))
        }
    }, [
        data, 
        agreeSign,
        someNomination,
        nominationIds, 
        t, 
        callback, 
        error
    ]);

    const defaultNominationId = useMemo(() => nominationIds?.[0] , [nominationIds])

    if (requestSended) {
        return <ProjectEditFrameCompetitionRequestSended onBackClick={() => modal.hide()}  />
    }

    return <ModalControllerContainer>
        <Wrapper direction='column' style={{ margin: isMobile ? '12px auto' : '88px auto', overflowY: "auto", overflowX: 'hidden' }} isGeo={isGeo && !isDafes} >
            <ContentContainer>
            <MainTitle>{t('competition_edit.form.form_name')}</MainTitle>
            <form ref={form} onSubmit={(e) => onSaveClick(e)} encType="multipart/form-data" id="submit-form">
                <input type='text' name='NominationId' value={defaultNominationId} hidden />
                <FlexBox justify={'center'} direction='column' directionMobile='column' offsetRight={20} offsetRightMobile={1}>
                    {!isGeniusPlaceCompetition && 
                                <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                                    <LegalsLabel>
                                        {t('competition_edit.form.nomination')}
                                        <LabelRedStar>*</LabelRedStar>
                                    </LegalsLabel>
                                    <EduSecetWrapper>
                                        <DamuseumDropdown 
                                            editing={true}
                                            onAddClick={(ev:any) => console.log('add', ev)}
                                            onChooseClick={(ev: any) => console.log('choose',ev)}
                                            currentValue={data.nomination}
                                            valueList={Object.values(nominationList)}
                                            onChange={handleFormFieldChange('nomination')}
                                        />
                                    </EduSecetWrapper>
                                </GridBox>
                    }
                    {!isGeniusPlaceCompetition && (
                        <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} style={{ marginBottom: isMobile ? "16px" : '24px', placeItems: "center start" }}>
                            <LegalsLabel>
                                {t('competition_edit.form.nomination')}
                                <LabelRedStar>*</LabelRedStar>
                            </LegalsLabel>
                            <EduSecetWrapper>
                                <DamuseumDropdown 
                                    editing={true}
                                    onAddClick={(ev:any) => console.log('add', ev)}
                                    onChooseClick={(ev: any) => console.log('choose',ev)}
                                    currentValue={data.subnomination} 
                                    placeholder='нет подноминаций'
                                    valueList={Object.values(subNominationsOptions)}
                                    onChange={handleFormFieldChange('subnomination')}
                                />
                            </EduSecetWrapper>
                        </GridBox>
                    )
                    }
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.' + (isGeniusPlaceCompetition? 'fullname' : 'names'))}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            required
                            onChange={handleFormFieldChange('names')}
                            value={data.names}
                            data-type="names"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.phone_number')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            type='tel'
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            pattern="[0-9]{1}[0-9]{3}[0-9]{3}[0-9]{4}"
                            onChange={handleFormFieldChange('phone')}
                            value={data.phone}
                            data-type="phone"
                            maxLength={100}
                            required
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.email')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            type={'email'}
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('email')}
                            value={data.email}
                            data-type="email"
                            maxLength={100}
                            required
                        />
                    </GridBox>
                    {!isGeniusPlaceCompetition && <>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.site')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('site')}
                            value={data.site}
                            data-type="site"
                            maxLength={100}
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.social')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('social')}
                            value={data.social}
                            data-type="social"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.portraitLink')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('portraitLink')}
                            value={data.portraitLink}
                            data-type="portraitLink"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.objectPhotoLink')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('objectPhotoLink')}
                            value={data.objectPhotoLink}
                            data-type="objectPhotoLink"
                            required
                            pattern='^(file|ftp|http|https):\/\/[^ "]+$'
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.media')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('media')}
                            value={data.media}
                            data-type="media"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.photographer')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('photographer')}
                            value={data.photographer}
                            data-type="photographer"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.shootingStylist')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('shootingStylist')}
                            value={data.shootingStylist}
                            data-type="shootingStylist"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.year')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            pattern='[0-9]{4}'
                            onChange={handleFormFieldChange('year')}
                            value={data.year}
                            data-type="year"
                            required
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.city')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('city')}
                            value={data.city}
                            data-type="city"
                            required
                            pattern='^[a-zA-Zа-яА-ЯёЁ\s-]+$'
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.area')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('area')}
                            value={data.area}
                            data-type="area"
                            required
                            pattern='^[a-zA-Zа-яА-ЯёЁ0-9\s\-.,⁰¹²³⁴⁵⁶⁷⁸⁹]+$'
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>{t('competition_edit.form.brands')}</LegalsLabel>
                        <Input.Text
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('brands')}
                            value={data.brands}
                            data-type="brands"
                        />
                    </GridBox>
                    <GridBox cols={ isMobile ? '1fr': "1fr 4fr"} rows={isMobile ? '' : ''} style={{ marginBottom: isMobile ? "16px" : '24px' , placeItems: "center start", gridAutoFlow: isMobile ? 'dense' : 'row' }}>
                        <LegalsLabel>
                            {t('competition_edit.form.description')}
                            <LabelRedStar>*</LabelRedStar>
                        </LegalsLabel>
                        <Input.Textarea
                            style={{
                                backgroundColor: theme.competition.input.bg,
                                color: theme.competition.input.mainText,
                            }}
                            onChange={handleFormFieldChange('description')}
                            value={data.description}
                            data-type="description"
                            required
                        />
                    </GridBox>
                    </>}
                </FlexBox>
                {!isGeniusPlaceCompetition && (
                    <CustomForm
                        onAgreeSign={(flag: boolean, data: any) => handleAgreeDataSign(flag, data)}
                        checkboxes={
                            [
                                {
                                    text: (language === 'ru' ? (
                                        <span>Я соглашаюсь с <a href={'https://deziiign.com/legals/offer'} target={'_BLANK'}>Пользовательским соглашением ресурсов Deziiign</a></span>
                                    ) : (
                                        <span>I agree to <a href={'https://deziiign.com/legals/offer'} target={'_BLANK'}>the User Agreement of Deziiign Resources</a></span>
                                    ))
                                },
                                {
                                    text: (language === 'ru' ? (
                                        <span>Я соглашаюсь с <a href={'https://deziiign.com/legals/privacy'} target={'_BLANK'}>Политикой конфиденциальности и обработки персональных данных ресурсов Deziiign</a></span>
                                    ) : (
                                        <span>I agree to <a href={'https://deziiign.com/legals/privacy'} target={'_BLANK'}>the Privacy Policy and processing of personal data of Deziiign resources</a></span>
                                    ))
                                },
                                {
                                    text: (language === 'ru' ? (
                                        <span>Я соглашаюсь с <a href={'https://www.interior.ru/design/19375.html'} target={'_BLANK'}>Политикой персональных данных ресурса Interior.ru</a></span>
                                    ) : (
                                        <span>I agree to <a href={'https://www.interior.ru/design/19375.html'} target={'_BLANK'}>the Personal Data Policy of the Interior.ru resource</a></span>
                                    ))
                                }
                            ]
                        }
                    />
                )}

                <FlexBox justify={'flex-end'} direction='row' directionMobile={isMobile ? 'column' : 'row'} offsetTop={40} offsetRight={20} offsetRightMobile={1}>
                    <SendButton isGreen={isGeo && !isDafes} type='submit' mr={"10"}>
                        Отправить
                    </SendButton>
                </FlexBox>
            </form>
            </ContentContainer>
        </Wrapper>
    </ModalControllerContainer>

};
